import { useLazyGetPitchConfigQuery } from '@frontend/core/lib/domains/pitches'
import type {
  HitTestArrow,
  PassChartArrow,
  PassChartArrowInput,
  PitchCoordinates
} from '@frontend/pitch'
import {
  AMERICAN_FOOTBALL_HASH_INSET_M,
  CANADIAN_FOOTBALL_HASH_INSET_M,
  computePassChartBounds,
  drawPassChart,
  hitTestElements,
  pitchLateralSpan,
  toPassChartArrow,
  usePitchCanvas
} from '@frontend/pitch'
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  type RefObject
} from 'react'
import { useAppSelector } from '../../store/hooks'
import { usePitchCanvasColors } from './hooks'

export const SportablePitch = () => {
  const { metricsServer } = useAppSelector((state) => state.servers.connections)

  const [getPitchConfig] = useLazyGetPitchConfigQuery()

  // Only get current pitch config on app load
  useEffect(() => {
    if (metricsServer.connected) {
      getPitchConfig()
    }
  }, [metricsServer.connected])

  return null
}

// ---------------------------------------------------------------------------
// PassChart
// ---------------------------------------------------------------------------
//
// Renders the QB-throw pass-chart view (AMF / CFL player detail). Every
// throw's Start X is collapsed onto a shared purple "zero" line, then the
// arrow is drawn in portrait display coords (forward distance up, lateral
// offset left/right). Clicking an arrow hit-tests in canvas space.
//
// Unit handling stays strictly at the caller: `formatDistance` receives
// metres and returns a display string (e.g. "5 yd" / "4.6 m"), and
// `gridSpacingMeters` lets the caller pick the gridline cadence matching
// whatever distance unit they've already committed to.

// Re-export the input arrow shape from @frontend/pitch so callers don't
// need two imports — one library-typed input, one React-typed.
export type { PassChartArrowInput } from '@frontend/pitch'

export interface PassChartProps {
  sport: 'american_football' | 'canadian_football'
  /** The real session pitch — P1..Pn corner coords from the reporting-api.
   *  Pass-chart lateral centring + width derive from this so sessions on
   *  any sized field (NFL / CFL / college / practice) line up correctly
   *  with the backend's direction-of-play normalisation. */
  pitch: PitchCoordinates
  /** Arrows to draw (usually the category-filtered subset). */
  arrows: PassChartArrowInput[]
  /** Arrow set used to compute the pitch bounds. Defaults to `arrows`.
   *  Pass the UNFILTERED arrow set so the pitch keeps a stable scale
   *  when the user toggles category pills on/off. */
  boundsArrows?: PassChartArrowInput[]
  selectedElementId?: string | null
  onElementClick?: (id: string) => void
  /** Called for each gridline. If omitted, lines render unlabelled. */
  formatDistance?: (meters: number) => string
  /** Gridline spacing in metres. Defaults to 5 yards. */
  gridSpacingMeters?: number
  /** Canvas ref exposed to parent for `canvasToImage` print snapshots. */
  canvasElRef?: RefObject<HTMLCanvasElement | null>
  className?: string
}

// Synthesise a 4-corner coordinate object from precomputed display-space
// bounds. `usePitchCanvas` wants `PitchCoordinates` so it can derive its
// own bounds internally — feeding it the rectangle's corners makes its
// computed bounds match ours exactly.
function boundsToCorners(bounds: {
  minX: number
  maxX: number
  minY: number
  maxY: number
}): PitchCoordinates {
  return {
    P1: { x: bounds.minX, y: bounds.minY, z: 0 },
    P2: { x: bounds.maxX, y: bounds.minY, z: 0 },
    P3: { x: bounds.maxX, y: bounds.maxY, z: 0 },
    P4: { x: bounds.minX, y: bounds.maxY, z: 0 }
  }
}

export function PassChart({
  sport,
  pitch,
  arrows,
  boundsArrows,
  selectedElementId,
  onElementClick,
  formatDistance,
  gridSpacingMeters,
  canvasElRef,
  className
}: PassChartProps) {
  const { fill, line } = usePitchCanvasColors()
  // Lateral pitch width comes from the session's real pitch geometry — never
  // from a hardcoded sport width — because the backend coord frame is
  // `Y ∈ [0, pitchWidth]` for whatever width the session was played on.
  const { widthM: pitchWidthM } = useMemo(
    () => pitchLateralSpan(pitch),
    [pitch]
  )
  // Hash-mark inset is a rule (NFL 23.5yd, CFL 24yd), not a pitch dimension,
  // so keep the sport-keyed constant.
  const hashInsetM =
    sport === 'canadian_football'
      ? CANADIAN_FOOTBALL_HASH_INSET_M
      : AMERICAN_FOOTBALL_HASH_INSET_M

  // Map raw world-space arrows into display-space pass-chart arrows once.
  const passChartArrows = useMemo<PassChartArrow[]>(() => {
    return arrows.map((a) => toPassChartArrow(a, pitchWidthM))
  }, [arrows, pitchWidthM])

  // Bounds use the unfiltered set when provided so the pitch scale stays
  // stable as the user toggles category pills. Falls back to the visible
  // arrows so the standalone case (no filtering) still works.
  const boundsSource = boundsArrows ?? arrows
  const passChartArrowsForBounds = useMemo<PassChartArrow[]>(() => {
    if (boundsSource === arrows) return passChartArrows
    return boundsSource.map((a) => toPassChartArrow(a, pitchWidthM))
  }, [boundsSource, arrows, passChartArrows, pitchWidthM])

  const bounds = useMemo(
    () => computePassChartBounds(passChartArrowsForBounds, { pitchWidthM }),
    [passChartArrowsForBounds, pitchWidthM]
  )

  const coordinates = useMemo(() => boundsToCorners(bounds), [bounds])

  const hasSelection = selectedElementId != null && selectedElementId !== ''

  const { canvasRef, containerRef, transform, redraw } = usePitchCanvas({
    coordinates,
    padding: 30,
    onDraw: (ctx, t, w, h) => {
      drawPassChart(ctx, passChartArrows, bounds, t.worldToCanvas, w, h, {
        fill,
        line,
        formatDistance,
        gridSpacingM: gridSpacingMeters,
        hashInsetM,
        selectedId: hasSelection ? (selectedElementId as string) : undefined
      })
    }
  })

  useImperativeHandle<HTMLCanvasElement | null, HTMLCanvasElement | null>(
    canvasElRef,
    () => canvasRef.current,
    [canvasRef]
  )

  // Redraw when data or selection changes.
  useEffect(() => {
    redraw()
  }, [passChartArrows, bounds, selectedElementId, redraw, fill, line])

  // Aspect ratio from bounds, exposed as a CSS custom property so the
  // default `aspect-[var(--pitch-ar)]` gives the pitch a portrait layout
  // on screens with no height context — and the caller can still blow
  // it away with `xl:aspect-auto` when the parent supplies an explicit
  // height (e.g. inside a fixed-height card).
  const aspectStyle = useMemo(() => {
    const widthM = Math.abs(bounds.maxX - bounds.minX) || 1
    const heightM = Math.abs(bounds.maxY - bounds.minY) || 1
    return { ['--pitch-ar' as string]: `${widthM} / ${heightM}` }
  }, [bounds])

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (!onElementClick || !transform) return
      const rect = canvasRef.current?.getBoundingClientRect()
      if (!rect) return
      const cx = e.clientX - rect.left
      const cy = e.clientY - rect.top

      const htArrows: HitTestArrow[] = passChartArrows.map((a) => {
        const from = transform.worldToCanvas(a.x, a.y)
        const to = transform.worldToCanvas(a.endX, a.endY)
        return {
          id: a.id,
          fromCx: from.cx,
          fromCy: from.cy,
          toCx: to.cx,
          toCy: to.cy
        }
      })
      const result = hitTestElements([], htArrows, cx, cy)
      if (result) {
        onElementClick(result.id === selectedElementId ? '' : result.id)
      } else if (hasSelection) {
        onElementClick('')
      }
    },
    [
      passChartArrows,
      transform,
      canvasRef,
      onElementClick,
      selectedElementId,
      hasSelection
    ]
  )

  // The container carries an intrinsic aspect ratio derived from the
  // pass-chart bounds so it lays out portrait on small screens with no
  // vertical context. When a parent supplies an explicit height (e.g.
  // `xl:h-full` inside a fixed-height card), the caller should also
  // pass `xl:aspect-auto` via `className` so the aspect ratio doesn't
  // force an overflow — `h-full` then fills the card and the canvas
  // transform letterboxes the pitch inside.
  return (
    <div
      ref={containerRef}
      className={['aspect-[var(--pitch-ar)] min-h-0 w-full', className]
        .filter(Boolean)
        .join(' ')}
      style={aspectStyle as React.CSSProperties}
    >
      <canvas
        ref={canvasRef}
        className='h-full w-full cursor-pointer'
        onClick={handleClick}
      />
    </div>
  )
}
